/**
 * Grouping a look-through kept as PATHS.
 *
 * `lookThroughContributions` answers one row per complete route from an entity
 * to a leaf stock — portfolio → model → fund → stock — with the route's share
 * of the entity (`pathWeight`) and, per metric family, that share times the
 * stock's own figure. Nothing here does look-through arithmetic: every number
 * on screen is a SUM of rows, which is what makes a tree and a flat list agree
 * with each other and with the entity's own figure.
 *
 * Three readings of the same rows, which is the Funds / Stocks / Both choice:
 *
 *   funds   the level-1 holdings, each the sum of every route through it
 *   stocks  every leaf stock, each the sum of every route that reaches it —
 *           a stock held through two funds is ONE row, the way it is one
 *           position in the book
 *   both    the tree: level-1 holdings that open onto what they hold, down to
 *           the stocks
 *
 * `pick(row)` is the figure being summed — a style contribution, a risk
 * contribution, a return — so the same grouping serves every family.
 */

const EMPTY = Object.freeze([]);

const sum = (rows, pick) => rows.reduce((t, r) => {
  const v = pick(r);
  return t + (Number.isFinite(v) ? v : 0);
}, 0);

/** The rows whose route begins with `prefix` (symbols drilled into so far). */
export const under = (rows, prefix) => (prefix.length
  ? rows.filter((r) => prefix.every((s, i) => r.path?.[i]?.securitySymbol === s))
  : rows);

/**
 * The nodes one level below `prefix`: group the rows under it on the next
 * path node, or on the stock where the route has no node left.
 *
 * Each node carries its share of the ENTITY (`weight`, Σ pathWeight), the
 * figure (`value`, Σ pick), and whether it opens further. `share` is the
 * node's share of its parent, for a reader who thinks in a fund's own terms.
 */
export function childrenAt(rows, prefix, pick) {
  const level = prefix.length;
  const scope = under(rows, prefix);
  const parentWeight = sum(scope, (r) => r.pathWeight) || 1;
  const groups = new Map();
  scope.forEach((r) => {
    const node = r.path?.[level];
    const leaf = !node || node.kind === 'stock';
    const key = leaf ? r.stockSymbol : node.securitySymbol;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        symbol: key,
        name: (leaf ? r.stockName : node.name) || null,
        kind: leaf ? 'stock' : node.kind,
        rows: [],
      });
    }
    groups.get(key).rows.push(r);
  });
  return [...groups.values()].map((g) => ({
    ...g,
    weight: sum(g.rows, (r) => r.pathWeight),
    share: sum(g.rows, (r) => r.pathWeight) / parentWeight,
    value: sum(g.rows, pick),
    /* Opens when any route under it has a node past this level that is not
       already the stock — a fund of funds, or a model's funds. */
    hasChildren: g.kind !== 'stock' && g.rows.some((r) => (r.path?.length ?? 0) > level + 1),
    unresolved: g.rows.some((r) => r.resolved === false),
  }));
}

/** Every leaf stock across every route, one row each. */
export function stocksOnly(rows, pick) {
  const groups = new Map();
  rows.forEach((r) => {
    const key = r.stockSymbol;
    if (!groups.has(key)) {
      groups.set(key, { key, symbol: key, name: r.stockName || null, kind: 'stock', rows: [] });
    }
    groups.get(key).rows.push(r);
  });
  return [...groups.values()].map((g) => ({
    ...g,
    weight: sum(g.rows, (r) => r.pathWeight),
    share: null,
    value: sum(g.rows, pick),
    hasChildren: false,
    unresolved: g.rows.some((r) => r.resolved === false),
    /* The level-1 holdings this stock is reached through — "via 3 funds". */
    via: [...new Set(g.rows.map((r) => r.path?.[0]?.securitySymbol).filter((s) => s && s !== g.key))],
  }));
}

/** Whether the rows have any level above the stock — i.e. the entity holds funds. */
export const hasFundLevel = (rows) => (rows ?? EMPTY).some((r) => (r.path ?? EMPTY).some((p) => p.kind !== 'stock'));

/** Largest magnitude first, ties by weight. */
export const byMagnitude = (a, b) => (Math.abs(b.value) - Math.abs(a.value)) || (b.weight - a.weight);

/** `pick` for a style factor: the row's contribution to that style. */
export const styleOf = (factor) => (r) => (r.exposureContributions ?? EMPTY).find((c) => c.factor === factor)?.exposure;

/** `pick` for the route's weight itself, as a percentage of the entity. */
export const weightOf = (r) => r.pathWeight * 100;

/** `valueScore` → `value_score`: the regression record's camelCase field as the look-through keys it. */
export const toSnake = (s) => String(s || '').replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/** `pick` for a regression loading, by the look-through's own key (`beta`, `value_score`, …). */
export const regressionOf = (key) => (r) => (r.regressionExposures ?? EMPTY).find((v) => v.key === key)?.value;

/**
 * The classification bucket an Exposure breakdown axis reads from.
 *
 * The breakdown's axes are the portfolio record's (`sectors`, `assetClasses`,
 * …); the look-through groups the same classifications server-side under its
 * own bucket names. Axes the look-through does not carry (region, currency)
 * map to nothing, and their buckets keep the flat drill.
 */
const AXIS_BUCKET = {
  sectors: 'sector', industries: 'industry', subIndustries: 'sub_industry', countries: 'country',
  assetClasses: 'asset_class', securityTypes: 'security_type',
};
export const axisBucket = (axisId) => AXIS_BUCKET[axisId] ?? null;

/**
 * The rows inside one bucket item: the server names them by pathId, so this
 * is a membership test, not a re-classification.
 */
export const inBucket = (rows, buckets, axisId, label) => {
  const bucket = buckets?.get(axisBucket(axisId));
  const item = bucket?.get(String(label ?? '').trim().toLowerCase());
  if (!item) return EMPTY;
  return (rows ?? EMPTY).filter((r) => item.pathIds.has(r.pathId));
};

/**
 * The entity itself as the root of every route.
 *
 * A mutual fund's rows begin at its holdings — the fund is the entity, not a
 * node — so its look-through has no fund level and the Funds / Stocks choice
 * had nothing to act on. Every other subject reads as fund → stock; so the
 * fund is put at the head of its own routes, which makes the three readings
 * mean the same thing on a fund page as on a model's: Funds is the fund as
 * one row, Funds & stocks is the fund open onto its stocks, Stocks is the
 * stocks. A fund of funds keeps its real nesting beneath that.
 *
 * Weights are untouched: the root's share of its parent is 1, so every
 * `pathWeight` is as it was.
 */
export const withRoot = (rows, root) => (rows ?? EMPTY).map((r) => ({
  ...r,
  path: [{ securitySymbol: root.securitySymbol, name: root.name ?? null, kind: root.kind ?? 'fund', weight: 1, level: 0 }, ...(r.path ?? EMPTY)],
}));

/* ── the risk families, as RISK_LOOK_THROUGH aliases them ─────────────────── */

const riskOf = (kind) => (r) => (kind === 'active' ? r.riskActive : r.riskTotal) ?? null;

/** `pick` for a route's whole contribution to the forecast σ of `kind`. */
export const riskTotalOf = (kind) => (r) => riskOf(kind)(r)?.totalContribution;

/** `pick` for a route's specific (stock-by-stock) part of the forecast σ. */
export const riskSpecificOf = (kind) => (r) => riskOf(kind)(r)?.specificContribution;

/** `pick` for a route's contribution to the forecast σ through one factor. */
export const riskFactorOf = (kind, factor) => (r) => (riskOf(kind)(r)?.byFactor ?? EMPTY)
  .find((c) => c.componentName === factor)?.contribution;

/**
 * `pick` for a route's share of one SIMULATION risk measure (`volatility`,
 * `var95`, `cvar95`, `expectedReturn`, …) under the scenario the rows were
 * read for. A fraction, like every stored forecast figure.
 */
export const simulationOf = (field) => (r) => r.simulationRisk?.[field];

/** The symbol the "Other" leaves are gathered under — no security has it. */
export const SIMULATION_OTHER = '__other__';

/** `own` / `unmapped` key their values in snake_case; the rows' fields are camelCase. */
const SIMULATION_KEYS = {
  volatility: 'volatility', var_95: 'var95', var_99: 'var99', cvar_95: 'cvar95', cvar_99: 'cvar99', expected_return: 'expectedReturn',
};
export const simulationValues = (values) => Object.fromEntries(
  (values ?? EMPTY).filter((v) => SIMULATION_KEYS[v.key]).map((v) => [SIMULATION_KEYS[v.key], Number(v.value)]),
);

/**
 * The served routes PLUS what they leave: one "Other" leaf under each level-1
 * holding, summing the priced leaves no route carries (`unmapped`, by `via`).
 *
 * This is the one place a look-through row is made on the client, and it is
 * still only a SUM: an Other's figures are its unmapped leaves' added up. With
 * them a fund's children add to the fund's own stored row and the funds to the
 * scenario's total. A level-1 STOCK no route carries is given back as itself
 * rather than as an Other of itself.
 */
export function simulationRoutes(served, unmapped) {
  const head = new Map();
  (served ?? EMPTY).forEach((r) => { const n = r.path?.[0]; if (n && !head.has(n.securitySymbol)) head.set(n.securitySymbol, n); });
  const others = new Map();
  const extra = [];
  (unmapped ?? EMPTY).forEach((u) => {
    const values = simulationValues(u.values);
    const weight = Number(u.weight) || 0;
    const via = u.via || u.securitySymbol;
    if (via === u.securitySymbol && u.kind === 'stock') {
      extra.push({
        pathId: `${via}>unmapped`,
        pathWeight: weight,
        resolved: true,
        stockSymbol: via,
        stockName: null,
        path: [{ securitySymbol: via, name: null, kind: 'stock', weight, level: 1 }],
        simulationRisk: values,
      });
      return;
    }
    const g = others.get(via) ?? { weight: 0, values: {}, count: 0 };
    g.weight += weight;
    g.count += 1;
    Object.entries(values).forEach(([k, v]) => { if (Number.isFinite(v)) g.values[k] = (g.values[k] ?? 0) + v; });
    others.set(via, g);
  });
  others.forEach((g, via) => {
    extra.push({
      pathId: `${via}>${SIMULATION_OTHER}`,
      pathWeight: g.weight,
      resolved: true,
      stockSymbol: SIMULATION_OTHER,
      stockName: 'Other — not looked through to a stock',
      path: [
        head.get(via) ?? { securitySymbol: via, name: null, kind: 'fund', weight: g.weight, level: 1 },
        {
          securitySymbol: SIMULATION_OTHER,
          name: `Other — ${g.count} ${g.count === 1 ? 'leaf' : 'leaves'} not looked through to a stock`,
          kind: 'stock',
          weight: 1,
          level: 2,
        },
      ],
      simulationRisk: g.values,
    });
  });
  return [...(served ?? EMPTY), ...extra];
}

/** `pick` for a route's share of the realised σ over the window. */
export const realizedOf = (r) => r.realizedRiskContribution;

/** `pick` for a route's contribution to the return over one horizon, in percentage points. */
export const returnOf = (horizon) => (r) => (r.returns ?? EMPTY).find((v) => v.key === horizon)?.value;

/* ── figures that AVERAGE rather than add: the valuation family ───────────── */

/**
 * A node's figure as the weighted mean of its rows' figures — harmonic where
 * the figure is a price multiple — over the rows that carry one.
 *
 * A P/E does not add: a fund's P/E is its stocks' earnings yields weighted,
 * inverted, which is the harmonic mean of their P/Es by weight. The weights
 * are the routes' shares of the ENTITY, so a node's mean and the entity's are
 * computed the same way and a reader can set one against the other.
 *
 * Returns `{ value, covered }`: the mean, and the share of the node's weight
 * the mean is priced on — a figure priced on a third of a fund is a different
 * thing from one priced on all of it, and the drill says which.
 */
export const meanOf = (harmonic) => Object.assign((rows, pick) => {
  let w = 0;
  let acc = 0;
  let all = 0;
  rows.forEach((r) => {
    const pw = Number(r.pathWeight) || 0;
    all += pw;
    const v = pick(r);
    if (!Number.isFinite(v) || (harmonic && v <= 0)) return;
    w += pw;
    acc += harmonic ? pw / v : pw * v;
  });
  if (!w) return { value: null, covered: 0 };
  return { value: harmonic ? w / acc : acc / w, covered: all ? w / all : 0 };
}, { harmonic: Boolean(harmonic) });

/**
 * The stored history behind a stock's fundamental, as the series registry
 * keys it — what a figure opens when it is clicked.
 *
 * A stock's valuation history is kept per STOCK, so the stock is the entity
 * and there is no holding scope. Only the multiples the history publishes are
 * here: debt to equity and earnings growth have no stored series, so those
 * figures stay plain rather than opening an empty chart. Keyed both ways —
 * the look-through's snake_case and the portfolio record's camelCase.
 */
const VALUATION_SERIES = {
  pe_ratio: 'valuation.pe', peRatio: 'valuation.pe',
  pb_ratio: 'valuation.pb', pbRatio: 'valuation.pb',
  dividend_yield: 'valuation.dividendYield', dividendYield: 'valuation.dividendYield',
  roe: 'valuation.roe',
};
export const valuationSeriesOf = (metric) => VALUATION_SERIES[metric] ?? null;

/** A stock master symbol — the only kind of holding a valuation history exists for. */
export const isStockSymbol = (symbol) => /\.(NSE|BSE)$/i.test(String(symbol || ''));

/** `pick` for a stock's own fundamental on a route (unweighted). */
export const fundamentalOf = (metric) => (r) => (r.fundamentals ?? EMPTY).find((f) => f.metric === metric)?.value;

/**
 * `childrenAt` / `stocksOnly` with an averaging aggregator instead of a sum:
 * the same nodes, each carrying `value` (the mean) and `covered`.
 */
const withMean = (nodes, pick, agg) => nodes.map((n) => {
  const { value, covered } = agg(n.rows, pick);
  return { ...n, value, covered };
});
export const childrenMeanAt = (rows, prefix, pick, agg) => withMean(childrenAt(rows, prefix, pick), pick, agg);
export const stocksMean = (rows, pick, agg) => withMean(stocksOnly(rows, pick), pick, agg);
